import type { ModuleDetailsProps, ModuleDetailsRef } from './module-details.props';
import React, {
  forwardRef,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { DynamicForm } from '@myosh/odin-components';
import type {
  DataGridRef,
  DialogRef,
  DropDownResult,
  DynamicFormButtonSetting,
  DynamicFormRef,
  DynamicFormSettings,
  JsonDataItem,
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
  OdinDataSender,
  OdinDataUpdate,
  OdinKey,
  PostSubmitHandler,
  PreSubmitHandler,
  RadioGroupResult,
} from '@myosh/odin-components';
import { v4 } from 'uuid';
import { skip, Subject, takeUntil } from 'rxjs';
import {
  useAddModuleMutation,
  useDeleteModuleMutation,
  useGetModuleByIdQuery,
  useModuleGroupsQuery,
  useUpdateModuleMutation,
} from '../../../redux/services/module';
import type { ActiveRecordContextProps } from '../../active-record/active-record.component';
import { getDataGridReference } from '../../../services/data-grid.service';
import { cloneDeep, debounce } from 'lodash';
import type {
  Module,
  ModuleFormData,
  ModuleMutationErrorResponse,
  ModuleMutationResponse,
  ModuleRole,
} from '../../../@types/modules';
import { formatFormData, getModuleFormSettings, getModuleTargetData, getModuleTypeData } from './module-form-settings';
import { useTranslation } from 'react-i18next';
import { FormTitle } from '../../form/form-title.component';
import CloseActiveItemButton from '../../common/close-active-item-button';
import { FormLoading } from '../../form/form-loading.component';
import { deleteButton, saveButton } from '../../../pages/admin/admin-utils';
import { useUserAccess } from '../../../hooks/use-user-access';
import { ModulesPermissions } from '../../../utils/user.permissions';
import { forceAssert } from '../../../utils/type-guards';
import useDynamicFormNotifier from '../../../hooks/use-dynamic-form-notifier';
import usePerformingSaveToast from '../../../hooks/use-performing-save-toast';
import usePerformingDeleteToast from '../../../hooks/use-performing-delete-toast';
import { showError, showSuccess } from '../../../services/notification.service';
import { useApiLogger } from '../../../hooks/use-api-logger';
import { useGetGroupsQuery } from '../../../redux/services/group';
import { getChangedFieldsData } from '../../../utils/user-functions';
import { getGroups } from '../../../utils/common-administration-utils';
import { ModuleFields } from '../../../utils/module-config';
import ConfirmationDialog from '../../common/confirmation-dialog.component';
import { api } from '../../../redux/services/api';
import { useAppDispatch } from '../../../redux/hooks';

const ModuleDetails = (props: ModuleDetailsProps, ref: Ref<ModuleDetailsRef>) => {
  const {
    moduleId,
    title,
    isNewModule = false,
    onClose,
    panelSettings,
    onCloseActiveRecordConfirmationModalRef,
  } = props;

  const dynamicFormReference = useRef<DynamicFormRef>(null);
  const dynamicFormId = useRef(v4());
  const dynamicFormData = useRef<JsonDataItem | undefined>(undefined);
  const destroySubject = useRef(new Subject<void>());
  const saveModalReference = useRef<DialogRef>(null);
  const deleteModalReference = useRef<DialogRef>(null);
  const dataGridRef = useRef<DataGridRef | undefined>(undefined);
  const batchSaveInProgressRef = useRef<boolean | undefined>(undefined);

  const [moduleData, setModuleData] = useState<ModuleFormData>();
  const [panelContextProps, setPanelContextProps] = useState<ActiveRecordContextProps>();
  const [formButtons, setFormButtons] = useState<Array<DynamicFormButtonSetting>>([]);

  const { t } = useTranslation();
  const log = useApiLogger();
  const dispatch = useAppDispatch();

  const [addModule] = useAddModuleMutation();
  const [deleteModule] = useDeleteModuleMutation();
  const [updateModule] = useUpdateModuleMutation();
  const { data, isLoading, isFetching } = useGetModuleByIdQuery(
    { moduleId: moduleId ?? 0 },
    {
      skip: !moduleId,
    }
  );
  const { data: moduleGroupOptions } = useModuleGroupsQuery();
  const { data: groupOptions } = useGetGroupsQuery({ externalUserSignupRoles: false });

  const { readOnly, editAccess } = useUserAccess({
    initialAccess: isNewModule,
    permissions: {
      MODIFY: [ModulesPermissions.MODULES_MODIFY, ModulesPermissions.MODULES_CREATE],
      READ: ModulesPermissions.MODULES_READ,
    },
  });

  const { notifyDirty, notifySaveSucceeded, notifySaveFailed } = useDynamicFormNotifier(dynamicFormId.current);

  const { showPerformingSaveToast, hidePerformingSaveToast } = usePerformingSaveToast({
    shouldFreezeActiveRecord: true,
    disabled: panelContextProps?.batchSaveInProgress,
  });
  const { showPerformingDeleteToast, hidePerformingDeleteToast } = usePerformingDeleteToast({
    shouldFreezeActiveRecord: true,
  });

  const handleSaveBtnClick = useCallback((skipValidation?: boolean) => {
    dynamicFormData.current = dynamicFormReference.current?.getData();
    dynamicFormReference.current?.submitForm(skipValidation ?? false);
  }, []);

  const onModalDeleteClick = useCallback(() => {
    deleteModalReference.current?.show();
  }, []);

  const handleModuleDelete = useCallback(() => {
    if (moduleId) {
      showPerformingDeleteToast();
      deleteModule(moduleId)
        .unwrap()
        .then(() => {
          hidePerformingDeleteToast();
          showSuccess(t('module-delete-success-message'));
          dispatch(api.util.invalidateTags(['NavigationItems']));
          onClose(dynamicFormId.current);
        })
        .catch(handleSaveFailedNotifications);
    }
  }, [moduleId, updateModule, showPerformingSaveToast, hidePerformingSaveToast, onClose, log]);

  useEffect(() => {
    panelSettings?.pipe(skip(1), takeUntil(destroySubject.current)).subscribe((settings) => {
      setPanelContextProps(settings);
      batchSaveInProgressRef.current = settings.batchSaveInProgress;
    });

    onCloseActiveRecordConfirmationModalRef
      ?.subscribeToDialogResult?.()
      ?.pipe(takeUntil(destroySubject.current))
      .subscribe((result) => {
        if (result.buttonName === 'save' && dynamicFormReference.current?.formIsDirty()) {
          handleSaveBtnClick(true);
        } else if (result.buttonName === 'discard') {
          onClose(dynamicFormId.current);
        }
      });

    getDataGridReference()
      .pipe(takeUntil(destroySubject.current))
      .subscribe((gridRef) => {
        if (gridRef) {
          dataGridRef.current = gridRef;
        }
      });

    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, []);

  useEffect(() => {
    if (data && !isFetching) {
      const moduleModified = cloneDeep<ModuleFormData>(data);
      moduleModified.type = forceAssert<RadioGroupResult>(getModuleTypeData(data.type)[0]);
      moduleModified.target = forceAssert<RadioGroupResult>(getModuleTargetData(data.target)[0]);

      if (moduleGroupOptions) {
        const selectedModuleGroup = moduleGroupOptions.find((option) => option.id === moduleModified.moduleGroupId);
        moduleModified.moduleGroupId = selectedModuleGroup
          ? { value: selectedModuleGroup.id, text: selectedModuleGroup.caption }
          : { value: moduleModified.moduleGroupId, text: '' };
      }

      if (groupOptions && moduleModified.roles) {
        const moduleRoles = moduleModified.roles as Array<ModuleRole>;
        moduleModified.roles = groupOptions
          .filter((option) => moduleRoles.some((role) => role.id === option.original.id))
          .map<DropDownResult>((role) => {
            return {
              value: role.original.id,
              text: role.original.caption,
            };
          });
      }

      setModuleData(moduleModified);
    }
  }, [data, moduleGroupOptions, groupOptions, isFetching]);

  useEffect(() => {
    if (!isFetching) {
      const _saveButton = cloneDeep(saveButton);
      _saveButton.onClick = handleSaveBtnClick;
      _saveButton.htmlType = 'button';

      const _deleteButton = cloneDeep(deleteButton);
      _deleteButton.onClick = onModalDeleteClick;

      const buttons = [_saveButton];

      if (moduleId && !isNewModule && editAccess) {
        buttons.push(_deleteButton);
      }

      setFormButtons(buttons);
    }
  }, [editAccess, isFetching, handleSaveBtnClick]);

  const onCloseUserDetails = () => {
    if (!dynamicFormReference.current?.formIsDirty()) {
      onClose(dynamicFormId.current);
    } else {
      saveModalReference.current?.show();
    }
  };

  const showSaveToast = () => {
    dispatch(api.util.invalidateTags(['NavigationItems']));
    hidePerformingSaveToast();
    if (!batchSaveInProgressRef.current) {
      showSuccess(t('record-saved'));
    }

    notifySaveSucceeded(dynamicFormId.current);

    onClose(dynamicFormId.current);
  };

  const saveModuleData = (moduleData: Partial<Module>, successCallback: (response?: unknown) => void) => {
    showPerformingSaveToast();
    if (moduleData.id) {
      updateModule({ id: moduleData.id, ...moduleData })
        .unwrap()
        .then(successCallback)
        .catch(handleSaveFailedNotifications);
    } else {
      addModule(moduleData).unwrap().then(successCallback).catch(handleSaveFailedNotifications);
    }
  };

  const showErrorToast = (message?: string) => {
    if (!batchSaveInProgressRef.current) {
      showError(message ?? t('module-details-save-failed'));
    }
    notifySaveFailed(dynamicFormId.current);
  };

  const handleSaveFailedNotifications = useCallback((error: ModuleMutationErrorResponse, showToast = true) => {
    hidePerformingSaveToast();
    if (showToast) {
      showErrorToast(error.data.validation.errorMessage);
    }

    log('Failed to update module', { error: error });
  }, []);

  const onFieldChanged = debounce(() => {
    notifyDirty(dynamicFormReference.current);
  }, 500);

  const onFormSubmit = useCallback(
    (data: JsonDataItem, sender: OdinDataSender<OdinKey | undefined>) => {
      if (!dynamicFormReference.current?.formIsDirty()) {
        showSaveToast();
      } else {
        if (moduleId && data) {
          const dirtyFields = dynamicFormReference.current?.formDirtyFields() || {};
          formatFormData(data);
          const patchData = getChangedFieldsData(data, dirtyFields, moduleId);

          saveModuleData(patchData, (response) => {
            const finalResponse = forceAssert<ModuleMutationResponse>(response);
            if (finalResponse?.result) {
              dataGridRef.current?.api.data.recordChanged(moduleId);
              showSaveToast();
            } else if (finalResponse?.data.validation.errors) {
              handleSaveFailedNotifications(finalResponse);
            }
          });
        } else if (!moduleId && data) {
          formatFormData(data);
          saveModuleData(data, (response) => {
            const finalResponse = forceAssert<ModuleMutationResponse>(response);
            if (finalResponse?.result) {
              sender.sendData(finalResponse.result);
              dataGridRef.current?.api.data.getDataOverwritePageCache();
              showSaveToast();
            } else if (finalResponse?.data.validation.errors) {
              handleSaveFailedNotifications(finalResponse);
            }
          });
        }
      }
    },
    [moduleId, moduleData, editAccess, dataGridRef]
  );

  const dataRetrieval: OdinDataRetrieval & OdinDataUpdate<JsonDataWrapper> = {
    getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if ('COMBOBOX' === options?.fieldType) {
        if (options.fieldId === ModuleFields.moduleGroupId && options.page === 1) {
          if (moduleGroupOptions) {
            const moduleGroups = moduleGroupOptions.map((option) => {
              return {
                value: option.id,
                text: option.caption,
              };
            });

            subscriber.sendData({ data: moduleGroups, requestId: options.requestId });
          }
        } else if (options.fieldId === ModuleFields.roles) {
          await getGroups(subscriber, options, false, 'id', 'caption');
        } else {
          subscriber.sendData();
        }
      } else if (options?.fieldType === 'OPTIONGROUP') {
        if (options.fieldId === ModuleFields.target && options.page === 1) {
          subscriber.sendData({ data: getModuleTargetData(), requestId: options.requestId });
        } else if (options.fieldId === ModuleFields.type && options.page === 1) {
          subscriber.sendData({ data: getModuleTypeData(), requestId: options.requestId });
        } else {
          subscriber.sendData();
        }
      } else {
        subscriber.sendData();
      }
    },
  };

  const onPreSubmit = useCallback<PreSubmitHandler>((_preSubmitData, formData) => {
    // Ensure onFormSubmit is called with the 'form data' and not the 'data proxy raw data'
    // Should be removed when MYOSH-5887 is implemented
    return dynamicFormData.current ?? formData;
  }, []);

  const onPostSubmit = useCallback<PostSubmitHandler>((id?: OdinKey) => {
    if (id) {
      hidePerformingSaveToast();
      showSaveToast();
    }
  }, []);

  const onGetPreSubmitData = () => {
    showPerformingSaveToast();
  };

  useImperativeHandle(ref, () => ({
    submitForm: handleSaveBtnClick,
  }));

  const moduleFormSettings = useMemo<DynamicFormSettings>(() => {
    return getModuleFormSettings();
  }, []);

  const onSaveCancel = (returnValue?: string) => {
    if (returnValue !== 'save') {
      onClose(dynamicFormId.current);
    }
  };

  return (
    <>
      <div className="flex flex-shrink-0 flex-col">
        <div className="flex">
          <FormTitle title={title} />
          {!isLoading && (
            <div className="flex min-w-min justify-end">
              <CloseActiveItemButton onClick={onCloseUserDetails} />
            </div>
          )}
        </div>
      </div>
      <div className="relative flex flex-grow flex-row overflow-y-hidden">
        {isLoading && <FormLoading />}
        {!isLoading && (
          <DynamicForm
            ref={dynamicFormReference}
            dynamicFormId={dynamicFormId.current}
            data={forceAssert<JsonDataItem>(moduleData ?? {})}
            settings={moduleFormSettings}
            dataRetrieval={dataRetrieval}
            readOnly={readOnly}
            buttons={formButtons}
            buttonsLocation={0}
            buttonsPosition={1}
            onSubmit={onFormSubmit}
            onPreSubmit={onPreSubmit}
            onPostSubmit={onPostSubmit}
            onGetPreSubmitData={onGetPreSubmitData}
            onFieldChanged={onFieldChanged}
          />
        )}
      </div>
      <ConfirmationDialog
        ref={saveModalReference}
        onConfirm={handleSaveBtnClick}
        onClose={onSaveCancel}
        cancelButtonText={t('discard')}
        hasCancelButton
      />
      <ConfirmationDialog
        ref={deleteModalReference}
        header={t('module-delete')}
        onConfirm={handleModuleDelete}
        onClose={onSaveCancel}
        confirmButtonText={t('yes')}
        cancelButtonText={t('no')}
        hasCancelButton
      >
        <p>{t('module-delete-confirmation')}</p>
      </ConfirmationDialog>
    </>
  );
};

export default forwardRef(ModuleDetails);
